import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Users, Tag, Sparkles, AlertTriangle, X, Search, Undo2, ArrowRight, ArrowLeft } from 'lucide-react';
export type RecipeFormData = {
  guests: string[];
  requirements: string[];
  requests: string[];
  avoids: string[];
  prompt: string;
  optionCount: number;
};
interface RecipeFormProps {
  onSubmit: (data: RecipeFormData) => void;
  isLoading: boolean;
  mobileStep: number | null; // null = desktop, 1-3 = mobile steps
  onMobileStepChange: (step: number) => void;
}
// Mock guest data
type Guest = {
  id: string;
  name: string;
  avatar: string;
  requirements: string[];
  requests: string[];
  avoids: string[];
};
const MOCK_GUESTS: Guest[] = [{
  id: '1',
  name: 'Alex',
  avatar: '👨‍🍳',
  requirements: ['Gluten-Free', 'Dairy-Free'],
  requests: ['High-Protein', 'Spicy'],
  avoids: ['Mushrooms', 'Cilantro']
}, {
  id: '2',
  name: 'Sam',
  avatar: '👩‍🍳',
  requirements: ['Vegetarian'],
  requests: ['Low-Carb', 'Extra-Cheese'],
  avoids: ['Onions']
}, {
  id: '3',
  name: 'Jordan',
  avatar: '🥘',
  requirements: ['Vegan'],
  requests: ['Spicy', 'Large-Portions'],
  avoids: ['Tofu', 'Eggplant']
}, {
  id: '4',
  name: 'Taylor',
  avatar: '🥗',
  requirements: [],
  requests: ['Healthy', 'Light'],
  avoids: ['Heavy-Sauces']
}, {
  id: '5',
  name: 'Casey',
  avatar: '🌮',
  requirements: ['Nut-Allergy'],
  requests: ['Comfort-Food'],
  avoids: ['Seafood']
}];
const PROMPT_SUGGESTIONS = ['A cozy dinner that feels like home', 'Something quick and easy for a weeknight', 'An impressive meal for entertaining guests', "Healthy comfort food that doesn't compromise on flavor", "A one-pot meal that's easy to clean up"];
type RemovedTag = {
  tag: string;
  type: 'requirements' | 'requests' | 'avoids';
  guestName: string;
};
type OpenSection = 'guests' | 'preferences' | 'prompt' | null;
export function RecipeForm({
  onSubmit,
  isLoading,
  mobileStep,
  onMobileStepChange
}: RecipeFormProps) {
  const [selectedGuests, setSelectedGuests] = useState<string[]>(['1', '2']);
  const [openSection, setOpenSection] = useState<OpenSection>('guests');
  const [guestSearch, setGuestSearch] = useState('');
  const [customRequirements, setCustomRequirements] = useState<string[]>([]);
  const [customRequests, setCustomRequests] = useState<string[]>([]);
  const [customAvoids, setCustomAvoids] = useState<string[]>([]);
  const [removedTags, setRemovedTags] = useState<RemovedTag[]>([]);
  const [prompt, setPrompt] = useState('');
  const [optionCount, setOptionCount] = useState(3);
  const [newTagInput, setNewTagInput] = useState('');
  const [activeTagSection, setActiveTagSection] = useState<'requirements' | 'requests' | 'avoids' | null>(null);
  const isMobile = mobileStep !== null;
  const aggregatePreferences = () => {
    const guests = MOCK_GUESTS.filter(g => selectedGuests.includes(g.id));
    const allRequirements = new Set<string>();
    const allRequests = new Set<string>();
    const allAvoids = new Set<string>();
    guests.forEach(guest => {
      guest.requirements.forEach(r => allRequirements.add(r));
      guest.requests.forEach(r => allRequests.add(r));
      guest.avoids.forEach(a => allAvoids.add(a));
    });
    customRequirements.forEach(r => allRequirements.add(r));
    customRequests.forEach(r => allRequests.add(r));
    customAvoids.forEach(a => allAvoids.add(a));
    removedTags.forEach(removed => {
      if (removed.type === 'requirements') allRequirements.delete(removed.tag);
      if (removed.type === 'requests') allRequests.delete(removed.tag);
      if (removed.type === 'avoids') allAvoids.delete(removed.tag);
    });
    return {
      requirements: Array.from(allRequirements),
      requests: Array.from(allRequests),
      avoids: Array.from(allAvoids)
    };
  };
  const prefs = aggregatePreferences();
  const toggleGuest = (guestId: string) => {
    setSelectedGuests(prev => prev.includes(guestId) ? prev.filter(id => id !== guestId) : [...prev, guestId]);
  };
  const toggleSection = (section: OpenSection) => {
    if (isMobile) return; // Don't allow toggling on mobile
    setOpenSection(openSection === section ? null : section);
  };
  const goToNextSection = (currentSection: OpenSection) => {
    if (isMobile) {
      // Mobile: advance to next step
      if (currentSection === 'guests') onMobileStepChange(2);else if (currentSection === 'preferences') onMobileStepChange(3);
    } else {
      // Desktop: open next section
      if (currentSection === 'guests') setOpenSection('preferences');else if (currentSection === 'preferences') setOpenSection('prompt');else if (currentSection === 'prompt') setOpenSection(null);
    }
  };
  const removeTag = (tag: string, type: 'requirements' | 'requests' | 'avoids') => {
    const guest = MOCK_GUESTS.find(g => selectedGuests.includes(g.id) && g[type].includes(tag));
    if (guest) {
      setRemovedTags(prev => [...prev, {
        tag,
        type,
        guestName: guest.name
      }]);
    } else {
      if (type === 'requirements') setCustomRequirements(prev => prev.filter(t => t !== tag));
      if (type === 'requests') setCustomRequests(prev => prev.filter(t => t !== tag));
      if (type === 'avoids') setCustomAvoids(prev => prev.filter(t => t !== tag));
    }
  };
  const undoRemoval = (removed: RemovedTag) => {
    setRemovedTags(prev => prev.filter(r => r !== removed));
  };
  const addCustomTag = (type: 'requirements' | 'requests' | 'avoids') => {
    if (!newTagInput.trim()) return;
    const tags = newTagInput.split(/[,;\s]+/).map(t => t.trim()).filter(Boolean);
    if (type === 'requirements') setCustomRequirements(prev => [...prev, ...tags]);
    if (type === 'requests') setCustomRequests(prev => [...prev, ...tags]);
    if (type === 'avoids') setCustomAvoids(prev => [...prev, ...tags]);
    setNewTagInput('');
    setActiveTagSection(null);
  };
  const suggestPrompt = () => {
    const random = PROMPT_SUGGESTIONS[Math.floor(Math.random() * PROMPT_SUGGESTIONS.length)];
    setPrompt(random);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      guests: selectedGuests,
      requirements: prefs.requirements,
      requests: prefs.requests,
      avoids: prefs.avoids,
      prompt,
      optionCount
    });
  };
  const filteredGuests = MOCK_GUESTS.filter(g => g.name.toLowerCase().includes(guestSearch.toLowerCase()));
  // Mobile: Show only current step
  if (isMobile) {
    return <form onSubmit={handleSubmit} className="w-full space-y-6" data-id="element-350">
        {/* Step 1: Guests */}
        {mobileStep === 1 && <div className="border-2 border-gray-300 bg-white p-6 space-y-4" data-id="element-351">
            <div className="space-y-2 mb-6" data-id="element-352">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-353">
                Select Guests
              </h2>
              <p className="text-sm text-gray-500 font-mono" data-id="element-354">
                Who are you cooking for today?
              </p>
            </div>

            <div className="relative" data-id="element-355">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-356" />
              <input type="text" placeholder="Search guests..." value={guestSearch} onChange={e => setGuestSearch(e.target.value)} className="w-full pl-10 pr-4 py-2 border-2 border-gray-300 font-mono text-sm focus:border-gray-500 focus:outline-none" data-id="element-357" />
            </div>

            <div className="grid grid-cols-2 gap-3" data-id="element-358">
              {filteredGuests.map(guest => <button key={guest.id} type="button" onClick={() => toggleGuest(guest.id)} className={`p-4 border-2 flex flex-col items-center gap-2 transition-all ${selectedGuests.includes(guest.id) ? 'border-gray-900 bg-gray-100' : 'border-gray-200 hover:border-gray-400'}`} data-id="element-359">
                  <div className="text-3xl" data-id="element-360">{guest.avatar}</div>
                  <span className="font-mono text-sm" data-id="element-361">{guest.name}</span>
                </button>)}
            </div>

            <div className="pt-4 flex justify-end" data-id="element-362">
              <button type="button" onClick={() => onMobileStepChange(2)} disabled={selectedGuests.length === 0} className="flex items-center px-6 py-3 bg-gray-900 text-white font-mono uppercase tracking-widest text-xs hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-363">
                Continue <ArrowRight className="ml-2 w-4 h-4" data-id="element-364" />
              </button>
            </div>
          </div>}

        {/* Step 2: Preferences */}
        {mobileStep === 2 && <div className="border-2 border-gray-300 bg-white p-6 space-y-6" data-id="element-365">
            <div className="space-y-2 mb-6" data-id="element-366">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-367">
                Dietary Preferences
              </h2>
              <p className="text-sm text-gray-500 font-mono" data-id="element-368">
                Review and adjust preferences from selected guests
              </p>
            </div>

            {/* Requirements */}
            <div className="space-y-3" data-id="element-369">
              <div className="flex items-center justify-between" data-id="element-370">
                <label className="font-mono text-xs uppercase tracking-wider text-red-600" data-id="element-371">
                  Requirements (Critical)
                </label>
                {activeTagSection !== 'requirements' && <button type="button" onClick={() => setActiveTagSection('requirements')} className="text-xs font-mono text-gray-500 hover:text-gray-700 border border-gray-300 px-2 py-1" data-id="element-372">
                    + Add
                  </button>}
              </div>

              {activeTagSection === 'requirements' && <div className="flex gap-2" data-id="element-373">
                  <input type="text" value={newTagInput} onChange={e => setNewTagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCustomTag('requirements'))} placeholder="Add requirement..." className="flex-1 border-2 border-gray-300 px-3 py-2 font-mono text-sm focus:border-gray-500 focus:outline-none" autoFocus data-id="element-374" />
                  <button type="button" onClick={() => addCustomTag('requirements')} className="px-4 py-2 bg-gray-900 text-white font-mono text-xs" data-id="element-375">
                    Add
                  </button>
                </div>}

              <div className="flex flex-wrap gap-2" data-id="element-376">
                {prefs.requirements.map(tag => <button key={tag} type="button" onClick={() => {
              if (window.confirm(`Remove critical requirement "${tag}"? This may cause issues for guests with allergies.`)) {
                removeTag(tag, 'requirements');
              }
            }} className="inline-flex items-center px-3 py-1 border-2 border-red-400 bg-red-50 text-sm font-mono text-red-700 hover:bg-red-100 group" data-id="element-377">
                    {tag}
                    <X className="w-3 h-3 ml-2 text-red-400 group-hover:text-red-600" data-id="element-378" />
                  </button>)}
              </div>
            </div>

            {/* Requests */}
            <div className="space-y-3" data-id="element-379">
              <div className="flex items-center justify-between" data-id="element-380">
                <label className="font-mono text-xs uppercase tracking-wider text-blue-600" data-id="element-381">
                  Requests (Preferences)
                </label>
                {activeTagSection !== 'requests' && <button type="button" onClick={() => setActiveTagSection('requests')} className="text-xs font-mono text-gray-500 hover:text-gray-700 border border-gray-300 px-2 py-1" data-id="element-382">
                    + Add
                  </button>}
              </div>

              {activeTagSection === 'requests' && <div className="flex gap-2" data-id="element-383">
                  <input type="text" value={newTagInput} onChange={e => setNewTagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCustomTag('requests'))} placeholder="Add request..." className="flex-1 border-2 border-gray-300 px-3 py-2 font-mono text-sm focus:border-gray-500 focus:outline-none" autoFocus data-id="element-384" />
                  <button type="button" onClick={() => addCustomTag('requests')} className="px-4 py-2 bg-gray-900 text-white font-mono text-xs" data-id="element-385">
                    Add
                  </button>
                </div>}

              <div className="flex flex-wrap gap-2" data-id="element-386">
                {prefs.requests.map(tag => <button key={tag} type="button" onClick={() => removeTag(tag, 'requests')} className="inline-flex items-center px-3 py-1 border border-blue-400 bg-blue-50 text-sm font-mono text-blue-700 hover:bg-blue-100 group" data-id="element-387">
                    {tag}
                    <X className="w-3 h-3 ml-2 text-blue-400 group-hover:text-blue-600" data-id="element-388" />
                  </button>)}
              </div>
            </div>

            {/* Avoids */}
            <div className="space-y-3" data-id="element-389">
              <div className="flex items-center justify-between" data-id="element-390">
                <label className="font-mono text-xs uppercase tracking-wider text-yellow-600" data-id="element-391">
                  Avoids (Dislikes)
                </label>
                {activeTagSection !== 'avoids' && <button type="button" onClick={() => setActiveTagSection('avoids')} className="text-xs font-mono text-gray-500 hover:text-gray-700 border border-gray-300 px-2 py-1" data-id="element-392">
                    + Add
                  </button>}
              </div>

              {activeTagSection === 'avoids' && <div className="flex gap-2" data-id="element-393">
                  <input type="text" value={newTagInput} onChange={e => setNewTagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCustomTag('avoids'))} placeholder="Add avoid..." className="flex-1 border-2 border-gray-300 px-3 py-2 font-mono text-sm focus:border-gray-500 focus:outline-none" autoFocus data-id="element-394" />
                  <button type="button" onClick={() => addCustomTag('avoids')} className="px-4 py-2 bg-gray-900 text-white font-mono text-xs" data-id="element-395">
                    Add
                  </button>
                </div>}

              <div className="flex flex-wrap gap-2" data-id="element-396">
                {prefs.avoids.map(tag => <button key={tag} type="button" onClick={() => removeTag(tag, 'avoids')} className="inline-flex items-center px-3 py-1 border border-yellow-400 bg-yellow-50 text-sm font-mono text-yellow-700 hover:bg-yellow-100 group" data-id="element-397">
                    {tag}
                    <X className="w-3 h-3 ml-2 text-yellow-400 group-hover:text-yellow-600" data-id="element-398" />
                  </button>)}
              </div>
            </div>

            {/* Removed Tags Warnings */}
            {removedTags.length > 0 && <div className="space-y-2 pt-4 border-t-2 border-dashed border-gray-200" data-id="element-399">
                {removedTags.map((removed, idx) => <div key={idx} className={`flex items-center justify-between p-3 border-2 ${removed.type === 'requirements' ? 'border-red-300 bg-red-50' : removed.type === 'requests' ? 'border-blue-200 bg-blue-50' : 'border-yellow-200 bg-yellow-50'}`} data-id="element-400">
                    <div className="flex items-center space-x-2" data-id="element-401">
                      <AlertTriangle className="w-4 h-4 text-orange-500" data-id="element-402" />
                      <span className="text-xs font-mono text-gray-700" data-id="element-403">
                        Removed {removed.guestName}'s{' '}
                        {removed.type.slice(0, -1)}:{' '}
                        <strong data-id="element-404">{removed.tag}</strong>
                      </span>
                    </div>
                    <button type="button" onClick={() => undoRemoval(removed)} className="flex items-center space-x-1 text-xs font-mono text-gray-600 hover:text-gray-900 border border-gray-300 px-2 py-1" data-id="element-405">
                      <Undo2 className="w-3 h-3" data-id="element-406" />
                      <span data-id="element-407">Undo</span>
                    </button>
                  </div>)}
              </div>}

            <div className="pt-4 flex items-center justify-between gap-3" data-id="element-408">
              <button type="button" onClick={() => onMobileStepChange(1)} className="flex items-center px-4 py-3 border-2 border-gray-300 hover:border-gray-500 transition-colors font-mono uppercase tracking-widest text-xs" data-id="element-409">
                <ArrowLeft className="w-4 h-4 mr-2" data-id="element-410" />
                Back
              </button>
              <button type="button" onClick={() => onMobileStepChange(3)} className="flex items-center px-6 py-3 bg-gray-900 text-white font-mono uppercase tracking-widest text-xs hover:bg-gray-800 transition-colors" data-id="element-411">
                Continue <ArrowRight className="ml-2 w-4 h-4" data-id="element-412" />
              </button>
            </div>
          </div>}

        {/* Step 3: Prompt */}
        {mobileStep === 3 && <div className="border-2 border-gray-300 bg-white p-6 space-y-4" data-id="element-413">
            <div className="space-y-2 mb-6" data-id="element-414">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-415">
                Recipe Prompt
              </h2>
              <p className="text-sm text-gray-500 font-mono" data-id="element-416">
                Describe what you'd like to cook
              </p>
            </div>

            <div className="flex items-center justify-between mb-2" data-id="element-417">
              <label className="font-mono text-xs uppercase tracking-wider text-gray-500" data-id="element-418">
                Your ideal recipe
              </label>
              <button type="button" onClick={suggestPrompt} className="flex items-center space-x-2 text-xs font-mono text-gray-500 hover:text-gray-700 border border-gray-300 px-3 py-1" data-id="element-419">
                <Sparkles className="w-3 h-3" data-id="element-420" />
                <span data-id="element-421">Suggest</span>
              </button>
            </div>
            <textarea value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="Describe what you'd like to cook... (e.g., 'A hearty pasta dish perfect for a cold evening')" rows={4} className="w-full border-2 border-gray-300 bg-transparent p-4 font-mono text-sm focus:border-gray-500 focus:outline-none placeholder:text-gray-300" data-id="element-422" />

            <div className="space-y-3" data-id="element-423">
              <label className="font-mono text-xs uppercase tracking-wider text-gray-500" data-id="element-424">
                Options to generate
              </label>
              <div className="flex items-center border-2 border-gray-300" data-id="element-425">
                <button type="button" onClick={() => setOptionCount(Math.max(1, optionCount - 1))} className="px-4 py-3 border-r-2 border-gray-300 hover:bg-gray-100 font-mono text-lg" data-id="element-426">
                  −
                </button>
                <span className="flex-1 text-center py-3 font-mono text-lg font-bold" data-id="element-427">
                  {optionCount}
                </span>
                <button type="button" onClick={() => setOptionCount(Math.min(5, optionCount + 1))} className="px-4 py-3 border-l-2 border-gray-300 hover:bg-gray-100 font-mono text-lg" data-id="element-428">
                  +
                </button>
              </div>
            </div>

            <div className="pt-4 space-y-3" data-id="element-429">
              <button type="submit" disabled={isLoading || selectedGuests.length === 0} className="w-full bg-gray-900 text-white font-mono uppercase tracking-widest py-4 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm" data-id="element-430">
                {isLoading ? '[ GENERATING... ]' : 'Generate Options'}
              </button>

              <button type="button" onClick={() => onMobileStepChange(2)} className="w-full flex items-center justify-center px-4 py-3 border-2 border-gray-300 hover:border-gray-500 transition-colors font-mono uppercase tracking-widest text-xs" data-id="element-431">
                <ArrowLeft className="w-4 h-4 mr-2" data-id="element-432" />
                Back
              </button>
            </div>
          </div>}
      </form>;
  }
  // Desktop: Original collapsible sections (unchanged)
  return <form onSubmit={handleSubmit} className="w-full space-y-6" data-id="element-433">
      {/* Guests Section */}
      <div className="border-2 border-gray-300 bg-white" data-id="element-434">
        <button type="button" onClick={() => toggleSection('guests')} className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors" data-id="element-435">
          <div className="flex items-center space-x-4" data-id="element-436">
            <Users className="w-5 h-5 text-gray-400" data-id="element-437" />
            <div className="text-left" data-id="element-438">
              <div className="font-mono text-xs uppercase tracking-wider text-gray-500 mb-1" data-id="element-439">
                01. Guests
              </div>
              {openSection !== 'guests' && <div className="flex items-center space-x-3" data-id="element-440">
                  <div className="flex -space-x-2" data-id="element-441">
                    {selectedGuests.slice(0, 4).map(id => {
                  const guest = MOCK_GUESTS.find(g => g.id === id);
                  return <div key={id} className="w-8 h-8 border-2 border-white bg-gray-100 flex items-center justify-center text-lg" data-id="element-442">
                          {guest?.avatar}
                        </div>;
                })}
                  </div>
                  <span className="text-sm text-gray-600" data-id="element-443">
                    {selectedGuests.length}{' '}
                    {selectedGuests.length === 1 ? 'person' : 'people'}
                  </span>
                </div>}
            </div>
          </div>
          {openSection === 'guests' ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-444" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-445" />}
        </button>

        {openSection === 'guests' && <div className="border-t-2 border-gray-200 p-6 space-y-4" data-id="element-446">
            <div className="relative" data-id="element-447">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-448" />
              <input type="text" placeholder="Search guests..." value={guestSearch} onChange={e => setGuestSearch(e.target.value)} className="w-full pl-10 pr-4 py-2 border-2 border-gray-300 font-mono text-sm focus:border-gray-500 focus:outline-none" data-id="element-449" />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-id="element-450">
              {filteredGuests.map(guest => <button key={guest.id} type="button" onClick={() => toggleGuest(guest.id)} className={`p-4 border-2 flex flex-col items-center gap-2 transition-all ${selectedGuests.includes(guest.id) ? 'border-gray-900 bg-gray-100' : 'border-gray-200 hover:border-gray-400'}`} data-id="element-451">
                  <div className="text-3xl" data-id="element-452">{guest.avatar}</div>
                  <span className="font-mono text-sm" data-id="element-453">{guest.name}</span>
                </button>)}
            </div>

            <div className="pt-4 flex justify-end" data-id="element-454">
              <button type="button" onClick={() => goToNextSection('guests')} disabled={selectedGuests.length === 0} className="flex items-center px-6 py-3 bg-gray-900 text-white font-mono uppercase tracking-widest text-xs hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-455">
                Next <ArrowRight className="ml-2 w-4 h-4" data-id="element-456" />
              </button>
            </div>
          </div>}
      </div>

      {/* Preferences Section */}
      <div className="border-2 border-gray-300 bg-white" data-id="element-457">
        <button type="button" onClick={() => toggleSection('preferences')} className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors" data-id="element-458">
          <div className="flex items-center space-x-4" data-id="element-459">
            <Tag className="w-5 h-5 text-gray-400" data-id="element-460" />
            <div className="text-left" data-id="element-461">
              <div className="font-mono text-xs uppercase tracking-wider text-gray-500 mb-1" data-id="element-462">
                02. Preferences
              </div>
              {openSection !== 'preferences' && <div className="flex items-center space-x-4 text-sm font-mono" data-id="element-463">
                  <span className="text-red-600" data-id="element-464">
                    {prefs.requirements.length} Requirements
                  </span>
                  <span className="text-blue-600" data-id="element-465">
                    {prefs.requests.length} Requests
                  </span>
                  <span className="text-yellow-600" data-id="element-466">
                    {prefs.avoids.length} Avoids
                  </span>
                </div>}
            </div>
          </div>
          {openSection === 'preferences' ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-467" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-468" />}
        </button>

        {openSection === 'preferences' && <div className="border-t-2 border-gray-200 p-6 space-y-6" data-id="element-469">
            {/* Requirements */}
            <div className="space-y-3" data-id="element-470">
              <div className="flex items-center justify-between" data-id="element-471">
                <label className="font-mono text-xs uppercase tracking-wider text-red-600" data-id="element-472">
                  Requirements (Critical)
                </label>
                {activeTagSection !== 'requirements' && <button type="button" onClick={() => setActiveTagSection('requirements')} className="text-xs font-mono text-gray-500 hover:text-gray-700 border border-gray-300 px-2 py-1" data-id="element-473">
                    + Add
                  </button>}
              </div>

              {activeTagSection === 'requirements' && <div className="flex gap-2" data-id="element-474">
                  <input type="text" value={newTagInput} onChange={e => setNewTagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCustomTag('requirements'))} placeholder="Add requirement..." className="flex-1 border-2 border-gray-300 px-3 py-2 font-mono text-sm focus:border-gray-500 focus:outline-none" autoFocus data-id="element-475" />
                  <button type="button" onClick={() => addCustomTag('requirements')} className="px-4 py-2 bg-gray-900 text-white font-mono text-xs" data-id="element-476">
                    Add
                  </button>
                </div>}

              <div className="flex flex-wrap gap-2" data-id="element-477">
                {prefs.requirements.map(tag => <button key={tag} type="button" onClick={() => {
              if (window.confirm(`Remove critical requirement "${tag}"? This may cause issues for guests with allergies.`)) {
                removeTag(tag, 'requirements');
              }
            }} className="inline-flex items-center px-3 py-1 border-2 border-red-400 bg-red-50 text-sm font-mono text-red-700 hover:bg-red-100 group" data-id="element-478">
                    {tag}
                    <X className="w-3 h-3 ml-2 text-red-400 group-hover:text-red-600" data-id="element-479" />
                  </button>)}
              </div>
            </div>

            {/* Requests */}
            <div className="space-y-3" data-id="element-480">
              <div className="flex items-center justify-between" data-id="element-481">
                <label className="font-mono text-xs uppercase tracking-wider text-blue-600" data-id="element-482">
                  Requests (Preferences)
                </label>
                {activeTagSection !== 'requests' && <button type="button" onClick={() => setActiveTagSection('requests')} className="text-xs font-mono text-gray-500 hover:text-gray-700 border border-gray-300 px-2 py-1" data-id="element-483">
                    + Add
                  </button>}
              </div>

              {activeTagSection === 'requests' && <div className="flex gap-2" data-id="element-484">
                  <input type="text" value={newTagInput} onChange={e => setNewTagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCustomTag('requests'))} placeholder="Add request..." className="flex-1 border-2 border-gray-300 px-3 py-2 font-mono text-sm focus:border-gray-500 focus:outline-none" autoFocus data-id="element-485" />
                  <button type="button" onClick={() => addCustomTag('requests')} className="px-4 py-2 bg-gray-900 text-white font-mono text-xs" data-id="element-486">
                    Add
                  </button>
                </div>}

              <div className="flex flex-wrap gap-2" data-id="element-487">
                {prefs.requests.map(tag => <button key={tag} type="button" onClick={() => removeTag(tag, 'requests')} className="inline-flex items-center px-3 py-1 border border-blue-400 bg-blue-50 text-sm font-mono text-blue-700 hover:bg-blue-100 group" data-id="element-488">
                    {tag}
                    <X className="w-3 h-3 ml-2 text-blue-400 group-hover:text-blue-600" data-id="element-489" />
                  </button>)}
              </div>
            </div>

            {/* Avoids */}
            <div className="space-y-3" data-id="element-490">
              <div className="flex items-center justify-between" data-id="element-491">
                <label className="font-mono text-xs uppercase tracking-wider text-yellow-600" data-id="element-492">
                  Avoids (Dislikes)
                </label>
                {activeTagSection !== 'avoids' && <button type="button" onClick={() => setActiveTagSection('avoids')} className="text-xs font-mono text-gray-500 hover:text-gray-700 border border-gray-300 px-2 py-1" data-id="element-493">
                    + Add
                  </button>}
              </div>

              {activeTagSection === 'avoids' && <div className="flex gap-2" data-id="element-494">
                  <input type="text" value={newTagInput} onChange={e => setNewTagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCustomTag('avoids'))} placeholder="Add avoid..." className="flex-1 border-2 border-gray-300 px-3 py-2 font-mono text-sm focus:border-gray-500 focus:outline-none" autoFocus data-id="element-495" />
                  <button type="button" onClick={() => addCustomTag('avoids')} className="px-4 py-2 bg-gray-900 text-white font-mono text-xs" data-id="element-496">
                    Add
                  </button>
                </div>}

              <div className="flex flex-wrap gap-2" data-id="element-497">
                {prefs.avoids.map(tag => <button key={tag} type="button" onClick={() => removeTag(tag, 'avoids')} className="inline-flex items-center px-3 py-1 border border-yellow-400 bg-yellow-50 text-sm font-mono text-yellow-700 hover:bg-yellow-100 group" data-id="element-498">
                    {tag}
                    <X className="w-3 h-3 ml-2 text-yellow-400 group-hover:text-yellow-600" data-id="element-499" />
                  </button>)}
              </div>
            </div>

            {/* Removed Tags Warnings */}
            {removedTags.length > 0 && <div className="space-y-2 pt-4 border-t-2 border-dashed border-gray-200" data-id="element-500">
                {removedTags.map((removed, idx) => <div key={idx} className={`flex items-center justify-between p-3 border-2 ${removed.type === 'requirements' ? 'border-red-300 bg-red-50' : removed.type === 'requests' ? 'border-blue-200 bg-blue-50' : 'border-yellow-200 bg-yellow-50'}`} data-id="element-501">
                    <div className="flex items-center space-x-2" data-id="element-502">
                      <AlertTriangle className="w-4 h-4 text-orange-500" data-id="element-503" />
                      <span className="text-xs font-mono text-gray-700" data-id="element-504">
                        Removed {removed.guestName}'s{' '}
                        {removed.type.slice(0, -1)}:{' '}
                        <strong data-id="element-505">{removed.tag}</strong>
                      </span>
                    </div>
                    <button type="button" onClick={() => undoRemoval(removed)} className="flex items-center space-x-1 text-xs font-mono text-gray-600 hover:text-gray-900 border border-gray-300 px-2 py-1" data-id="element-506">
                      <Undo2 className="w-3 h-3" data-id="element-507" />
                      <span data-id="element-508">Undo</span>
                    </button>
                  </div>)}
              </div>}

            <div className="pt-4 flex justify-end" data-id="element-509">
              <button type="button" onClick={() => goToNextSection('preferences')} className="flex items-center px-6 py-3 bg-gray-900 text-white font-mono uppercase tracking-widest text-xs hover:bg-gray-800 transition-colors" data-id="element-510">
                Next <ArrowRight className="ml-2 w-4 h-4" data-id="element-511" />
              </button>
            </div>
          </div>}
      </div>

      {/* Prompt Section */}
      <div className="border-2 border-gray-300 bg-white" data-id="element-512">
        <button type="button" onClick={() => toggleSection('prompt')} className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors" data-id="element-513">
          <div className="flex items-center space-x-4" data-id="element-514">
            <Sparkles className="w-5 h-5 text-gray-400" data-id="element-515" />
            <div className="text-left" data-id="element-516">
              <div className="font-mono text-xs uppercase tracking-wider text-gray-500 mb-1" data-id="element-517">
                03. Recipe Prompt
              </div>
              {openSection !== 'prompt' && prompt && <p className="text-sm text-gray-600 truncate max-w-md" data-id="element-518">
                  {prompt}
                </p>}
            </div>
          </div>
          {openSection === 'prompt' ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-519" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-520" />}
        </button>

        {openSection === 'prompt' && <div className="border-t-2 border-gray-200 p-6 space-y-4" data-id="element-521">
            <div className="flex items-center justify-between mb-2" data-id="element-522">
              <label className="font-mono text-xs uppercase tracking-wider text-gray-500" data-id="element-523">
                Describe your ideal recipe
              </label>
              <button type="button" onClick={suggestPrompt} className="flex items-center space-x-2 text-xs font-mono text-gray-500 hover:text-gray-700 border border-gray-300 px-3 py-1" data-id="element-524">
                <Sparkles className="w-3 h-3" data-id="element-525" />
                <span data-id="element-526">Suggest</span>
              </button>
            </div>
            <textarea value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="Describe what you'd like to cook... (e.g., 'A hearty pasta dish perfect for a cold evening')" rows={3} className="w-full border-2 border-gray-300 bg-transparent p-4 font-mono text-sm focus:border-gray-500 focus:outline-none placeholder:text-gray-300" data-id="element-527" />

            <div className="pt-4 flex items-center justify-between" data-id="element-528">
              <div className="flex items-center space-x-3" data-id="element-529">
                <span className="font-mono text-xs text-gray-500" data-id="element-530">
                  Options to generate:
                </span>
                <div className="flex items-center border-2 border-gray-300" data-id="element-531">
                  <button type="button" onClick={() => setOptionCount(Math.max(1, optionCount - 1))} className="px-3 py-1 border-r-2 border-gray-300 hover:bg-gray-100 font-mono text-sm" data-id="element-532">
                    −
                  </button>
                  <span className="px-4 py-1 font-mono text-sm font-bold" data-id="element-533">
                    {optionCount}
                  </span>
                  <button type="button" onClick={() => setOptionCount(Math.min(5, optionCount + 1))} className="px-3 py-1 border-l-2 border-gray-300 hover:bg-gray-100 font-mono text-sm" data-id="element-534">
                    +
                  </button>
                </div>
              </div>

              <button type="button" onClick={() => goToNextSection('prompt')} className="flex items-center px-6 py-3 bg-gray-900 text-white font-mono uppercase tracking-widest text-xs hover:bg-gray-800 transition-colors" data-id="element-535">
                Done <ArrowRight className="ml-2 w-4 h-4" data-id="element-536" />
              </button>
            </div>
          </div>}
      </div>

      {/* Generate Button */}
      <button type="submit" disabled={isLoading || selectedGuests.length === 0} className="w-full bg-gray-900 text-white font-mono uppercase tracking-widest py-5 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm" data-id="element-537">
        {isLoading ? '[ GENERATING OPTIONS... ]' : 'Generate Options'}
      </button>
    </form>;
}